<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { HYBRID_QR_STATE } from 'dashboard/helper/whatsappHybrid';

const props = defineProps({
  state: { type: String, required: true },
  phoneLast4: { type: String, default: '' },
  checkedAt: { type: String, default: null },
});

const { t } = useI18n();

const APPEARANCE = {
  [HYBRID_QR_STATE.CONNECTED]: {
    icon: 'i-lucide-circle-check',
    classes: 'bg-n-teal-3 text-n-teal-11',
  },
  [HYBRID_QR_STATE.WAITING]: {
    icon: 'i-lucide-qr-code',
    classes: 'bg-n-amber-3 text-n-amber-11',
  },
  [HYBRID_QR_STATE.DISCONNECTED]: {
    icon: 'i-lucide-circle-x',
    classes: 'bg-n-ruby-3 text-n-ruby-11',
  },
  [HYBRID_QR_STATE.NOT_CONFIGURED]: {
    icon: 'i-lucide-circle-dashed',
    classes: 'bg-n-alpha-2 text-n-slate-11',
  },
};

const appearance = computed(
  () => APPEARANCE[props.state] || APPEARANCE[HYBRID_QR_STATE.NOT_CONFIGURED]
);

const label = computed(() => {
  switch (props.state) {
    case HYBRID_QR_STATE.CONNECTED:
      return t('INBOX_MGMT.EDIT.HYBRID_QR.STATUS.CONNECTED', {
        last4: props.phoneLast4,
      });
    case HYBRID_QR_STATE.WAITING:
      return t('INBOX_MGMT.EDIT.HYBRID_QR.STATUS.WAITING');
    case HYBRID_QR_STATE.DISCONNECTED:
      return t('INBOX_MGMT.EDIT.HYBRID_QR.STATUS.DISCONNECTED');
    default:
      return t('INBOX_MGMT.EDIT.HYBRID_QR.STATUS.NOT_CONFIGURED');
  }
});

const checkedLabel = computed(() => {
  if (!props.checkedAt) return '';
  const minutes = Math.floor(
    (Date.now() - Date.parse(props.checkedAt)) / 60000
  );
  if (Number.isNaN(minutes)) return '';
  if (minutes < 1) return t('INBOX_MGMT.EDIT.HYBRID_QR.CHECKED.NOW');
  if (minutes < 60)
    return t('INBOX_MGMT.EDIT.HYBRID_QR.CHECKED.MINUTES', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 48)
    return t('INBOX_MGMT.EDIT.HYBRID_QR.CHECKED.HOURS', { count: hours });
  return t('INBOX_MGMT.EDIT.HYBRID_QR.CHECKED.DAYS', {
    count: Math.floor(hours / 24),
  });
});
</script>

<template>
  <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
    <span
      role="status"
      data-test-id="hybrid-qr-status"
      class="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium"
      :class="appearance.classes"
    >
      <span class="size-4 flex-shrink-0" :class="appearance.icon" />
      {{ label }}
    </span>
    <span v-if="checkedLabel" class="text-xs text-n-slate-11">
      {{ checkedLabel }}
    </span>
  </div>
</template>
